import { TOTAL_PLACEHOLDER } from '../constants';

// A constraint prompt may carry several ';'-separated phrases, spoken one per ask.
export const PROMPT_PHRASE_SEPARATOR = ';';

// A prompt of separators alone is one empty phrase, so its semicolons are never spoken.
export const splitPromptPhrases = (prompt?: string | null): string[] => {
  const phrases = (prompt ?? '')
    .split(PROMPT_PHRASE_SEPARATOR)
    .map((phrase) => phrase.trim())
    .filter((phrase) => phrase.length);
  return phrases.length || !prompt ? phrases : [''];
};

// Whether a send can speak this text: a `{total}` template needs a check total.
export const isPromptSpeakable = (text: string, hasCheckTotal: boolean) =>
  hasCheckTotal || !text.includes(TOTAL_PLACEHOLDER);

export interface IPromptPhrasePick {
  phrase: string;
  // The slot's ask count once this phrase is spoken.
  nextAskIndex: number;
}

// The AI's re-ask ladder (each phrase once, then the first from ask N on), stepping over unspeakable phrases; none speakable keeps the ladder's phrase and consumes no ask.
export const pickPromptPhrase = (
  phrases: string[],
  askIndex = 0,
  hasCheckTotal = true
): IPromptPhrasePick | undefined => {
  if (!phrases.length) return undefined;
  const checkpoint = phrases.length;
  const isSpeakable = (phrase: string) =>
    isPromptSpeakable(phrase, hasCheckTotal);
  for (let index = askIndex; index < checkpoint; index++) {
    if (isSpeakable(phrases[index])) {
      return { phrase: phrases[index], nextAskIndex: index + 1 };
    }
  }
  const checkpointIndex = phrases.findIndex(isSpeakable);
  if (checkpointIndex >= 0) {
    return {
      phrase: phrases[checkpointIndex],
      nextAskIndex: Math.max(askIndex, checkpoint) + 1,
    };
  }
  return {
    phrase: phrases[askIndex < checkpoint ? askIndex : 0],
    nextAskIndex: askIndex,
  };
};
